import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api';
import { useT } from '../i18n';
import type { EntryStatus, EventStatus, ItemStatus } from '../types';

export function PageHead({ title, sub, actions, back }: { title: ReactNode; sub?: ReactNode; actions?: ReactNode; back?: { to: string; label: string } }) {
  return (
    <div className="page-head">
      <div>
        {back && (
          <Link className="back" to={back.to}>
            ← {back.label}
          </Link>
        )}
        <h1>{title}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </div>
  );
}

export function Modal({ title, onClose, children, footer, wide }: { title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const t = useT();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="btn btn-ghost icon-btn" onClick={onClose} aria-label={t('common.close')}>
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, hint, children, full, htmlFor }: { label: string; hint?: string; children: ReactNode; full?: boolean; htmlFor?: string }) {
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

/** Shows an API error in the user's language, with details lists where useful. */
export function ErrorBox({ error }: { error: unknown }) {
  const t = useT();
  if (!error) return null;
  if (error instanceof ApiError) {
    const key = `err.${error.code}`;
    const msg = t(key) === key ? error.message : t(key);
    const details = Array.isArray(error.details) ? (error.details as Record<string, unknown>[]) : null;
    return (
      <div className="alert alert-error" role="alert">
        {msg}
        {details && details.length > 0 && (
          <ul>
            {details.slice(0, 12).map((d, i) => (
              <li key={i}>{describeDetail(d)}</li>
            ))}
            {details.length > 12 && <li>… +{details.length - 12}</li>}
          </ul>
        )}
      </div>
    );
  }
  return <div className="alert alert-error">{String((error as Error)?.message ?? error)}</div>;
}

function describeDetail(d: Record<string, unknown>) {
  if (d.field) return `${d.field}: ${d.message}`;
  const parts = [d.qrCode, d.name ?? d.itemName, d.eventTitle ? `→ ${d.eventTitle}` : null, d.kitName ? `(${d.kitName})` : null, d.status].filter(Boolean);
  return parts.join(' ');
}

export function Loading() {
  const t = useT();
  return <div className="loading">{t('common.loading')}</div>;
}

export function Empty({ title, text, action }: { title: string; text?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {text && <p>{text}</p>}
      {action}
    </div>
  );
}

const EVENT_TONE: Record<EventStatus, string> = {
  draft: '',
  confirmed: 'badge-blue',
  in_progress: 'badge-green',
  completed: 'badge-ink',
  invoiced: 'badge-ink',
  cancelled: 'badge-red',
};
export function EventStatusBadge({ status }: { status: EventStatus }) {
  const t = useT();
  return <span className={`badge ${EVENT_TONE[status]}`}>{t(`eventStatus.${status}`)}</span>;
}

const ITEM_TONE: Record<ItemStatus, string> = {
  available: 'badge-green',
  out: 'badge-blue',
  in_repair: 'badge-amber',
  lost: 'badge-red',
  retired: '',
};
export function ItemStatusBadge({ status }: { status: ItemStatus }) {
  const t = useT();
  return <span className={`badge ${ITEM_TONE[status]}`}>{t(`itemStatus.${status}`)}</span>;
}

const ENTRY_TONE: Record<EntryStatus, string> = { open: 'badge-blue', submitted: 'badge-amber', approved: 'badge-green', rejected: 'badge-red' };
export function EntryStatusBadge({ status }: { status: EntryStatus }) {
  const t = useT();
  return <span className={`badge ${ENTRY_TONE[status]}`}>{t(`entryStatus.${status}`)}</span>;
}

export function Tabs<K extends string>({ tabs, value, onChange }: { tabs: { key: K; label: string; count?: number }[]; value: K; onChange: (k: K) => void }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button key={tab.key} role="tab" aria-selected={value === tab.key} className={value === tab.key ? 'active' : ''} onClick={() => onChange(tab.key)}>
          {tab.label}
          {tab.count !== undefined && <span className="count">{tab.count}</span>}
        </button>
      ))}
    </div>
  );
}
